import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { addExperimentToMicroLearning, removeExperimentFromMicroLearning } from '../../../api/api'
import { ExperimentLinkTarget, ExperimentMutationResult } from '../../../api/models'
import { AppStateContext } from '../../../state/AppProvider'

export const useExperimentManagement = (mlId: string, onChanged: () => void) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const isMountedRef = useRef(true)
  const appStateContext = useContext(AppStateContext)
  const dispatchRef = useRef(appStateContext?.dispatch)
  dispatchRef.current = appStateContext?.dispatch

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const notify = useCallback((message: string, severity: 'success' | 'warning' | 'error') => {
    dispatchRef.current?.({
      type: 'SET_SNACKBAR_STATE',
      payload: { message, severity },
    })
  }, [])

  const runMutation = useCallback(
    async (
      mutation: () => Promise<ExperimentMutationResult>,
      successMessage: string,
      defaultErrorMessage: string,
      errorSeverity: 'warning' | 'error' = 'error',
    ) => {
      if (isSubmittingRef.current) return false
      isSubmittingRef.current = true
      setIsSubmitting(true)

      try {
        const result = await mutation()
        if (result.success) {
          // The refresh callback failing must not turn a successful mutation into an error toast.
          try {
            onChanged()
          } catch (err) {
            console.error('Experiment list refresh failed after a successful mutation.', err)
          }
          notify(successMessage, 'success')
          return true
        }

        notify(result.error ?? defaultErrorMessage, errorSeverity)
        return false
      } catch {
        notify(defaultErrorMessage, 'error')
        return false
      } finally {
        isSubmittingRef.current = false
        if (isMountedRef.current) {
          setIsSubmitting(false)
        }
      }
    },
    [notify, onChanged],
  )

  const addExperiment = useCallback(
    async (experimentCode: string, target: ExperimentLinkTarget) =>
      runMutation(
        () => addExperimentToMicroLearning(mlId, experimentCode, target),
        `Experiment ${experimentCode} added.`,
        'Could not add experiment. Please try again.',
        'warning',
      ),
    [mlId, runMutation],
  )

  const removeExperiment = useCallback(
    async (experimentCode: string, target: ExperimentLinkTarget) =>
      runMutation(
        () => removeExperimentFromMicroLearning(mlId, experimentCode, target),
        `Experiment ${experimentCode} removed.`,
        'Could not remove experiment. Please try again.',
      ),
    [mlId, runMutation],
  )

  return { isSubmitting, addExperiment, removeExperiment }
}
